import React, { useCallback, useContext, useEffect, useState } from 'react';
import { querystringConf } from '../utils/';
import {
	INotificationContextState,
	notificationContext,
} from './notification.context';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import { useNotificationPermission } from '../hooks/useNotificationPermission';
import { configContext } from './config/context';
import { useLogError } from '../components/ErrorBoundary/useLogError';

const usePushNotificationWorker = () => {
	const { notificationEnabled, baseUrl } = useContext(configContext);
	const permission = useNotificationPermission(notificationEnabled);

	const logError = useLogError('usePushNotificationWorker');

	const hide_notification =
		querystringConf('hide_notification') ?? !notificationEnabled;

	const [registration, setRegistration] = useState<ServiceWorkerRegistration>();

	const registerPushNotification = useCallback(
		() =>
			serviceWorkerRegistration
				.register(baseUrl, 'pushNotification')
				.then((registration) => {
					if (registration) setRegistration(registration);
					else {
						throw new Error('Missing registration');
					}
				})
				.catch((error: Error) =>
					logError('registerPushNotification', { error }),
				),
		[baseUrl, logError],
	);
	const unregisterNotification = useCallback(() => {
		serviceWorkerRegistration
			.unregister()
			.catch((error: Error) =>
				logError('unregisterPushNotification', { error }),
			);
		setRegistration(undefined);
	}, [logError]);

	useEffect(() => {
		if (!hide_notification && permission) {
			if (permission !== 'granted') {
				unregisterNotification();
			} else {
				registerPushNotification();
			}
		} else {
			console.log('ServiceWorker blocked by env');
		}
	}, [
		registerPushNotification,
		permission,
		unregisterNotification,
		hide_notification,
	]);

	return { registration, permission };
};

export const NotificationProvider = ({
	children,
}: React.PropsWithChildren<{}>) => {
	const { registration, permission } = usePushNotificationWorker();

	const logError = useLogError('NotificationProvider');

	const hide_notification = querystringConf('hide_notification');
	useEffect(() => {
		if (permission !== 'granted') {
			console.warn('Notification disabled or permission request prompted');
		}
		if (!registration) {
			console.warn('Worker not registered');
		}
	}, [permission, registration]);

	const sendNotification = useCallback<
		INotificationContextState['sendNotification']
	>(
		(event) => {
			if (hide_notification) {
				const { title, options } = event.payload ?? {};
				const { badge, icon, body, ...rest } = options ?? {};
				console.groupCollapsed(
					'Show Notification',
					'(Blocked by development ENV)',
				);
				console.info('Title:', title);
				console.info('Body:', body);
				console.log('Options:', { ...rest, badge: !!badge, icon: !!icon });
				console.groupEnd();
			} else {
				try {
					if (permission !== 'granted' || !registration) return;
					if (!document.hasFocus()) {
						console.log('Notification sent to the worker');
						registration?.active?.postMessage(event);
					}
				} catch (error: any) {
					logError('sendNotification', { error });
				}
			}
		},
		[registration, permission, hide_notification, logError],
	);

	return (
		<notificationContext.Provider value={{ permission, sendNotification }}>
			{children}
		</notificationContext.Provider>
	);
};
